import {
  Typography,
  TextField,
  Box,
  Stack,
  Switch,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Controller, useForm } from 'react-hook-form';
import { WAutocomplete, WButton } from '@wareclouds/wareclouds-ui';
import { useBankAccountForm } from '../../application';
import {
  COUNTRY_CONFIG,
  PIX_KEY_TYPE_VALUES,
  pixKeyTypes,
  FORM_MODES,
  getCnaeOptions,
  formatBrPixPhone,
} from '../../domain';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect } from 'react';
import { useBankAccountFormSyncBr } from '../hooks/useBankAccountFormSyncBr';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CONTEXT } from '@/config/consts/commons';
import { formatCNPJ, formatCPF } from '@wareclouds/frontend-common';
import { BankAccountCnpjBanner } from './BankAccountCnpjBanner';
import { BankAccountFormSkeleton } from './BankAccountFormSkeleton';

const inputStyles = {
  fontSize: '1.2rem',
  '& .MuiInputBase-root': { height: '47px' },
};

const responsiveRowSpacing = { xs: 3, md: 5 };

const gridStyle = { xs: 12, md: 4 };

export const BankAccountFormBr = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['form', 'validation']);
  const countryConfig = COUNTRY_CONFIG[CONTEXT];
  const cnaeOptions = getCnaeOptions(t);
  const [formMode, setFormMode] = useState(FORM_MODES.CNPJ);
  const isCnpjMode = formMode === FORM_MODES.CNPJ;

  const {
    banksData,
    accounts,
    requiresCnpj,
    isLoadingDataBankList,
    isSavingData,
    isLoadingExistingData,
    onSubmit,
  } = useBankAccountForm({ countryConfig });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(countryConfig.createSchema(t)),
    context: { formMode },
    defaultValues: countryConfig.initialValues,
    mode: 'onChange',
  });

  const selectedBank = watch('bank');
  const selectedAccountType = watch('accountType');
  const pixKeyType = watch('pixKeyType');

  const {
    isLoadBlocking,
    isSaveDisabled,
    isLoadingBankData,
    handleNoCnpjChange,
    showNoCnpjSwitch,
  } = useBankAccountFormSyncBr({
    accounts,
    requiresCnpj,
    banksData,
    selectedBank,
    selectedAccountType,
    reset,
    setValue,
    clearErrors,
    isSubmitting,
    isSavingData,
    isLoadingExistingData,
    isLoadingDataBankList,
    formMode,
    setFormMode,
  });

  useEffect(() => {
    if (pixKeyType) {
      trigger('pixKey');
    }
  }, [pixKeyType, trigger]);

  if (isLoadingExistingData || isLoadingDataBankList) {
    return (
      <BankAccountFormSkeleton
        showBanner
        showSwitch
        fieldCount={8}
        gridSize={gridStyle}
      />
    );
  }

  return (
    <>
      <Box>
        <Typography
          variant="body1"
          component="h1"
          mb={1}
          sx={{ fontWeight: 400, fontSize: 24 }}
        >
          {t('bank_info.title')}
        </Typography>
        <Typography mb={2} component="p">
          {t('bank_info.description')}
        </Typography>
      </Box>

      <BankAccountCnpjBanner t={t} recommend={!requiresCnpj} />

      {showNoCnpjSwitch && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
          <Switch
            checked={formMode === FORM_MODES.CPF}
            onChange={handleNoCnpjChange}
            disabled={isSavingData || isSubmitting}
            sx={{
              mr: 1,
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c38c8' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#5c38c8',
                opacity: 0.55,
              },
            }}
          />
          <Typography sx={{ fontSize: 16 }}>
            {t('bank_info.no_cnpj')}
          </Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container rowSpacing={responsiveRowSpacing} columnSpacing={4}>
          <Grid size={gridStyle}>
            <Controller
              name="document"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <TextField
                  {...field}
                  value={value}
                  disabled={isLoadingBankData || isLoadBlocking}
                  label={isCnpjMode ? 'CNPJ' : 'CPF'}
                  placeholder={
                    isCnpjMode
                      ? t('bank_info.document.placeholder_cnpj')
                      : t('bank_info.document.placeholder_cpf')
                  }
                  variant="standard"
                  fullWidth
                  error={!!errors.document}
                  helperText={errors.document?.message}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    onChange(
                      isCnpjMode
                        ? formatCNPJ(inputValue)
                        : formatCPF(inputValue)
                    );
                  }}
                  sx={inputStyles}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          </Grid>

          {isCnpjMode && (
            <>
              <Grid size={gridStyle}>
                <Controller
                  name="legalName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled={isLoadingBankData || isLoadBlocking}
                      label={t('bank_info.legal_name.label')}
                      placeholder={t('bank_info.legal_name.placeholder')}
                      variant="standard"
                      fullWidth
                      error={!!errors.legalName}
                      helperText={errors.legalName?.message}
                      sx={inputStyles}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />
              </Grid>

              <Grid size={gridStyle}>
                <Controller
                  name="fiscalAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled={isLoadingBankData || isLoadBlocking}
                      label={t('bank_info.fiscal_address.label')}
                      placeholder={t('bank_info.fiscal_address.placeholder')}
                      variant="standard"
                      fullWidth
                      error={!!errors.fiscalAddress}
                      helperText={errors.fiscalAddress?.message}
                      sx={inputStyles}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />
              </Grid>

              <Grid size={gridStyle}>
                <WAutocomplete
                  name="cnae"
                  control={control}
                  options={cnaeOptions}
                  textFieldProps={{
                    label: t('bank_info.cnae.label'),
                    placeholder: t('bank_info.cnae.placeholder'),
                    variant: 'standard',
                    fullWidth: true,
                    sx: inputStyles,
                    InputLabelProps: { shrink: true },
                  }}
                  autocompleteProps={{
                    disabled: isLoadingBankData || isLoadBlocking,
                    isOptionEqualToValue: (option, value) =>
                      option.value === value?.value,
                  }}
                  translateError={(error) => error?.message}
                />
              </Grid>
            </>
          )}

          <Grid size={gridStyle}>
            <WAutocomplete
              name="bank"
              control={control}
              options={banksData || []}
              loading={isLoadingBankData}
              textFieldProps={{
                label: t('bank_info.bank.label'),
                placeholder: t('bank_info.bank.placeholder'),
                variant: 'standard',
                fullWidth: true,
                sx: inputStyles,
                InputLabelProps: { shrink: true },
              }}
              autocompleteProps={{
                disabled: isLoadingBankData || isLoadBlocking,
                isOptionEqualToValue: (option, value) =>
                  option.label === value?.label,
              }}
              translateError={(error) => error?.message}
            />
          </Grid>

          <Grid size={gridStyle}>
            <WAutocomplete
              name="accountType"
              control={control}
              options={selectedBank?.accountTypes || []}
              loading={isLoadingBankData}
              textFieldProps={{
                label: t('bank_info.account_type.label'),
                placeholder: t('bank_info.account_type.placeholder'),
                variant: 'standard',
                fullWidth: true,
                sx: inputStyles,
                InputLabelProps: { shrink: true },
              }}
              autocompleteProps={{
                disabled: isLoadingBankData || isLoadBlocking || !selectedBank,
                isOptionEqualToValue: (option, value) =>
                  option.label === value?.label,
              }}
              translateError={(error) => error?.message}
            />
          </Grid>

          <Grid size={gridStyle}>
            <Controller
              name="agencyCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isLoadingBankData || isLoadBlocking}
                  label={t('bank_info.agency_code.label')}
                  placeholder={t('bank_info.agency_code.placeholder')}
                  variant="standard"
                  fullWidth
                  error={!!errors.agencyCode}
                  helperText={errors.agencyCode?.message}
                  sx={inputStyles}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          </Grid>

          <Grid size={gridStyle}>
            <Controller
              name="accountNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isLoadingBankData || isLoadBlocking}
                  label={t('bank_info.account_number.label')}
                  placeholder={t('bank_info.account_number.placeholder')}
                  variant="standard"
                  fullWidth
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber?.message}
                  sx={inputStyles}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          </Grid>

          <Grid size={gridStyle}>
            <WAutocomplete
              name="pixKeyType"
              control={control}
              options={pixKeyTypes}
              loading={isLoadingBankData}
              textFieldProps={{
                label: t('bank_info.pix_key_type.label'),
                placeholder: t('bank_info.pix_key_type.placeholder'),
                variant: 'standard',
                fullWidth: true,
                sx: inputStyles,
                InputLabelProps: { shrink: true },
                error: !!errors.pixKeyType,
                helperText: errors.pixKeyType?.message,
              }}
              autocompleteProps={{
                disabled: isLoadingBankData || isLoadBlocking,
                isOptionEqualToValue: (option, value) =>
                  option.label === value?.label,
              }}
              translateError={(error) => error?.message}
            />
          </Grid>

          <Grid size={gridStyle}>
            <Controller
              name="pixKey"
              control={control}
              render={({ field: { onChange, value, ...field } }) => {
                const currentPixKeyType = pixKeyType?.value;

                return (
                  <TextField
                    {...field}
                    value={value}
                    disabled={isLoadingBankData || isLoadBlocking}
                    onChange={(e) => {
                      const inputValue = e.target.value;

                      if (currentPixKeyType === PIX_KEY_TYPE_VALUES.CPF) {
                        onChange(formatCPF(inputValue));
                      } else if (
                        currentPixKeyType === PIX_KEY_TYPE_VALUES.CNPJ
                      ) {
                        onChange(formatCNPJ(inputValue));
                      } else if (
                        currentPixKeyType === PIX_KEY_TYPE_VALUES.PHONE
                      ) {
                        onChange(formatBrPixPhone(inputValue));
                      } else {
                        onChange(inputValue);
                      }
                    }}
                    label={t('bank_info.pix_key.label')}
                    placeholder={t('bank_info.pix_key.placeholder')}
                    variant="standard"
                    fullWidth
                    error={!!errors.pixKey}
                    helperText={t(errors.pixKey?.message)}
                    sx={inputStyles}
                    slotProps={{
                      inputLabel: { shrink: true },
                      input:
                        currentPixKeyType === PIX_KEY_TYPE_VALUES.PHONE
                          ? {
                              startAdornment: (
                                <InputAdornment position="start">
                                  +55
                                </InputAdornment>
                              ),
                            }
                          : undefined,
                    }}
                  />
                );
              }}
            />
          </Grid>
        </Grid>

        <Stack
          mt={6}
          direction={{ xs: 'column-reverse', md: 'row' }}
          spacing={2}
          justifyContent={{ md: 'space-between' }}
        >
          <WButton
            label={t('button:back')}
            color="secondary"
            variant="contained"
            sx={{
              color: '#000',
              bgcolor: 'grey.300',
              '&:hover': {
                bgcolor: 'grey.300',
              },
              width: { xs: '100%', md: '201px' },
            }}
            onClick={() => navigate('/')}
          />
          <WButton
            label={isSavingData ? t('button:saving') : t('button:save')}
            variant="contained"
            type="submit"
            isDisabled={isSaveDisabled || !isDirty}
            sx={{ width: { xs: '100%', md: '201px' } }}
          />
        </Stack>
      </form>
    </>
  );
};
